import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Menu, X, Navigation, Heart, LogOut, User, Store, Wrench } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { CitySelector } from "@/components/CitySelector";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Explore", to: "/explore" },
  { label: "Services", to: "/services" },
  { label: "Near Me", to: "/near-me" },
  { label: "List Your Business", to: "/list-business" },
  { label: "List Your Service", to: "/list-service" },
  { label: "Get Your Website", to: "/get-your-website" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const navigate = useNavigate();
  const { customer, loginWithGoogle, logout } = useCustomerAuth();
  const [city, setCity] = useState(localStorage.getItem("oyebest_city") || "etah");
  const [cityName, setCityName] = useState(localStorage.getItem("oyebest_city_name") || "Etah");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selectCity = (slug, name) => {
    setCity(slug); setCityName(name);
    localStorage.setItem("oyebest_city", slug);
    localStorage.setItem("oyebest_city_name", name);
    navigate(`/${slug}`);
  };
  const doSearch = (e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}&city=${city}`); };

  const startLogin = () => { localStorage.setItem("oyebest_post_login", window.location.pathname); loginWithGoogle(); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <span className="font-display text-2xl font-extrabold tracking-tight">Oye<span className="text-primary">Best</span></span>
          </Link>

          <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-md items-center bg-secondary rounded-full px-4 h-11" data-testid="header-search-form">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Restaurant, Hotel, Salon, Shoes…" className="bg-transparent outline-none px-3 text-sm w-full" data-testid="header-search-input" />
          </form>

          <nav className="hidden lg:flex items-center gap-5">
            <Link to="/explore" className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors" data-testid="nav-explore">Explore</Link>
            <Link to="/services" className="flex items-center gap-1 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors" data-testid="nav-services"><Wrench className="w-3.5 h-3.5" /> Services</Link>
            <Link to="/near-me" className="flex items-center gap-1 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors" data-testid="nav-near-me"><Navigation className="w-3.5 h-3.5" /> Near Me</Link>
          </nav>

          <div className="flex items-center gap-2">
            <CitySelector valueSlug={city} valueName={cityName} onSelect={selectCity} align="end" />

            <Link to="/list-business" className="hidden md:block">
              <Button className="rounded-full font-semibold h-10 px-5" data-testid="header-list-business">List Your Business</Button>
            </Link>

            {customer ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border border-border" data-testid="profile-menu">
                    {customer.picture ? <img src={customer.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-primary" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5 text-sm"><p className="font-semibold truncate">{customer.name}</p><p className="text-xs text-muted-foreground truncate">{customer.email}</p></div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/favourites")} data-testid="menu-favourites"><Heart className="w-4 h-4 mr-2" /> My Favourites</DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" onClick={startLogin} className="rounded-full h-10 px-4 font-semibold hidden sm:flex" data-testid="header-login-btn">Login</Button>
            )}

            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <form onSubmit={doSearch} className="md:hidden pb-3 flex items-center bg-secondary rounded-full px-4 h-11">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Restaurant, Hotel, Salon, Shoes…" className="bg-transparent outline-none px-3 text-sm w-full" data-testid="mobile-search-input" />
        </form>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="block py-2.5 text-sm font-semibold text-foreground/80 hover:text-primary">{n.label}</Link>
          ))}
          <Link to="/owner" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2.5 text-sm font-semibold text-foreground/80 hover:text-primary"><Store className="w-4 h-4" /> Business Owner Login</Link>
          {customer ? (
            <>
              <Link to="/favourites" onClick={() => setOpen(false)} className="block py-2.5 text-sm font-semibold text-foreground/80 hover:text-primary">My Favourites</Link>
              <button onClick={() => { setOpen(false); logout(); }} className="block py-2.5 text-sm font-semibold text-foreground/80 hover:text-primary">Logout</button>
            </>
          ) : (
            <button onClick={() => { setOpen(false); startLogin(); }} className="block py-2.5 text-sm font-semibold text-primary" data-testid="mobile-login-btn">Login with Google</button>
          )}
        </div>
      )}
    </header>
  );
}
